import { ButtonFrame, ExportData, PageBodyHeader } from "../../component";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux";
import { fetchOrderTrackerDetail, fetchStoreListByUserId, fetchUserListById, fetchUserListByStoreId } from "../../action/generalFunc";
import { generalObj } from "../../constant/object_types";
import * as XLSX from "xlsx-js-style";

const formatMonth = (value:string) => {
    const [year, month] = value.replaceAll("_", "-").split("-");
    const monthDate = new Date(Number(year), Number(month) - 1);
    return Number.isNaN(monthDate.getTime()) ? value : monthDate.toLocaleString("en-US", {month:"long", year:"numeric"});
};

const formatNumber = (value:number | string | undefined | null) => value === undefined || value === null || value === "" || value === "-" ? "-" : Number(value).toLocaleString("en-US", {maximumFractionDigits: 2});

const isFilled = (value:number | string | undefined | null) => {
    if (value === undefined || value === null) return false;
    const str = String(value).trim();
    return str !== "" && str !== "-" && str !== "0";
};

const AdminOrderTrackerOverview = () => {
    const navigate = useNavigate();
    const { date = "", supervisor_id = "" } = useParams();
    const account = useSelector((state:RootState) => state.account);
    const { setModalLoading }: { setModalLoading:Function } = useOutletContext();
    const [supervisorName, setSupervisorName] = useState<string>("-");
    const [stores, setStores] = useState<generalObj[]>([]);
    const [vendors, setVendors] = useState<generalObj[]>([]);
    const [searchText, setSearchText] = useState<string>("");

    useEffect(() => {
        if (!account || !account.isAuthenticated || !supervisor_id || !date) return;

        const loadOverview = async () => {
            setModalLoading(true);
            const orderTrackerDate = date.replaceAll("_", "-");
            const [supervisorResult, storeResult] = await Promise.all([
                fetchUserListById(account.token, supervisor_id),
                fetchStoreListByUserId(account.token, supervisor_id, "staff", {order_tracker: orderTrackerDate})
            ]);

            if (!storeResult) {
                setModalLoading(false);
                return;
            }

            const firstname = supervisorResult?.data?.user?.firstname || supervisorResult?.firstname || "";
            const lastname = supervisorResult?.data?.user?.lastname || supervisorResult?.lastname || "";

            setSupervisorName(`${firstname} ${lastname}`.trim());
            const storeList = storeResult.data?.stores || [];
            const storeData = await Promise.all(storeList.map(async (store:generalObj) => {
                const result = await fetchUserListByStoreId(account.token, String(store.id), "vendor", {order_tracker: orderTrackerDate});
                const storeVendors = result?.data?.users || [];
                const vendorData = await Promise.all(storeVendors.map(async (vendor:generalObj) => {
                    const detailResult = await fetchOrderTrackerDetail(account.token, String(vendor.id), String(store.id), date.replaceAll("-", "_"));
                    return {...vendor, details: Array.isArray(detailResult) ? detailResult : []};
                }));
                return {...store, vendors: vendorData};
            }));

            const vendorMap:{[key:string]: generalObj} = {};
            storeData.forEach((store:generalObj) => store.vendors.forEach((vendor:generalObj) => {
                vendorMap[vendor.id] = vendor;
            }));
            setStores(storeData);
            setVendors(Object.values(vendorMap));
            setModalLoading(false);
        };

        loadOverview();
    }, [account, date, supervisor_id, setModalLoading]);

    const visibleStores = stores.filter((store) => `${store.name} ${store.organization}`.toLowerCase().includes(searchText.toLowerCase()));
    const getVendorForStore = (store:generalObj, vendorId:string | number) => store.vendors.find((vendor:generalObj) => vendor.id === vendorId);
    const getOrder = (vendor:generalObj | undefined) => {
        const details = vendor?.details;
        
        if (!details || details.length === 0) return {
            "po_number"    : "",
            "order_value"  : null,
            "order_date"   : ""
        };

        const poNumbers = details.map((detail:generalObj) => detail.po_number).filter((v:any) => v !== null && v !== undefined && String(v).trim() !== "" && String(v).trim() !== "-").join(", ");
        const orderDates = details.map((detail:generalObj) => detail.order_date).filter((v:any) => v !== null && v !== undefined && String(v).trim() !== "" && String(v).trim() !== "-").join(", ");
        const totalValue = details.reduce((sum: number, detail: generalObj) => sum + (Number(detail.order_value) || 0), 0);

        return {
            "po_number"    : poNumbers,
            "order_value"  : totalValue > 0 ? totalValue : null,
            "order_date"   : orderDates
        };
    };

    const exportOverview = (exportType:string) => {
        if (exportType !== "excel") return;

        const rows:(string | number)[][] = [
            ["NO", supervisorName, ...vendors.flatMap((vendor) => [vendor.username, "", "", ""])],
            ["", "", ...vendors.flatMap(() => ["TARGET", "P.O NUMBER", "VALUE", "ORDER DATE"])]
        ];

        visibleStores.forEach((store, index) => {
            rows.push([
                index + 1,
                store.name,
                ...vendors.flatMap((vendor) => {
                    const storeVendor = getVendorForStore(store, vendor.id);
                    const tracker = storeVendor?.order_tracker?.[0];
                    const order = getOrder(storeVendor);
                    return [
                        formatNumber(tracker?.target),
                        order.po_number || "-",
                        formatNumber(order.order_value),
                        order.order_date || "-"
                    ];
                })
            ]);
        });

        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        const setCellStyle = (row:number, column:number, style:object) => {
            const cell = worksheet[XLSX.utils.encode_cell({r:row, c:column})];
            if (cell) cell.s = style;
        };
        const border = {style:"thin", color:{rgb:"222222"}};
        const baseHeaderStyle = {border:{top:border, bottom:border, left:border, right:border}, alignment:{horizontal:"center", vertical:"center"}, font:{bold:true}};
        const noHeaderStyle = {...baseHeaderStyle, fill:{fgColor:{rgb:"FFFFFF"}}};
        const supervisorHeaderStyle = {...baseHeaderStyle, fill:{fgColor:{rgb:"FFE69A"}}, font:{bold:true, color:{rgb:"CC0000"}}};
        const vendorHeaderStyle = {...baseHeaderStyle, fill:{fgColor:{rgb:"FFC107"}}, font:{bold:true, sz:14}};
        const targetHeaderStyle = {...baseHeaderStyle, fill:{fgColor:{rgb:"E2F0D9"}}, font:{bold:true, color:{rgb:"008800"}}};
        const detailHeaderStyle = {...baseHeaderStyle, fill:{fgColor:{rgb:"FFF2CC"}}, font:{bold:true, color:{rgb:"111111"}}};
        const bodyStyle = {border:{top:border, bottom:border, left:border, right:border}, alignment:{vertical:"top"}};
        const targetBodyStyle = {...bodyStyle, fill:{fgColor:{rgb:"E2F0D9"}}, alignment:{horizontal:"right", vertical:"top"}};
        const numberBodyStyle = {...bodyStyle, alignment:{horizontal:"right", vertical:"top"}};
        const filledBodyStyle = {...bodyStyle, fill:{fgColor:{rgb:"E2F0D9"}}};
        const emptyBodyStyle = {...bodyStyle, fill:{fgColor:{rgb:"FCE4E4"}}};
        const filledNumberBodyStyle = {...numberBodyStyle, fill:{fgColor:{rgb:"E2F0D9"}}};
        const emptyNumberBodyStyle = {...numberBodyStyle, fill:{fgColor:{rgb:"FCE4E4"}}};

        setCellStyle(0, 0, noHeaderStyle);
        setCellStyle(0, 1, supervisorHeaderStyle);
        setCellStyle(1, 0, noHeaderStyle);
        setCellStyle(1, 1, supervisorHeaderStyle);
        vendors.forEach((_, vendorIndex) => {
            const startColumn = 2 + vendorIndex * 4;
            setCellStyle(0, startColumn, vendorHeaderStyle);
            [0, 1, 2, 3].forEach((detailIndex) => setCellStyle(1, startColumn + detailIndex, detailIndex === 0 ? targetHeaderStyle : detailHeaderStyle));
        });
        visibleStores.forEach((store, storeIndex) => {
            const rowIndex = storeIndex + 2;
            setCellStyle(rowIndex, 0, bodyStyle);
            setCellStyle(rowIndex, 1, bodyStyle);
            vendors.forEach((vendor, vendorIndex) => {
                const storeVendor = getVendorForStore(store, vendor.id);
                const order = getOrder(storeVendor);
                const startColumn = 2 + vendorIndex * 4;
                setCellStyle(rowIndex, startColumn, targetBodyStyle);
                setCellStyle(rowIndex, startColumn + 1, isFilled(order.po_number) ? filledBodyStyle : emptyBodyStyle);
                setCellStyle(rowIndex, startColumn + 2, isFilled(order.order_value) ? filledNumberBodyStyle : emptyNumberBodyStyle);
                setCellStyle(rowIndex, startColumn + 3, isFilled(order.order_date) ? filledBodyStyle : emptyBodyStyle);
            });
        });
        worksheet["!merges"] = [
            {s:{r:0, c:0}, e:{r:1, c:0}},
            {s:{r:0, c:1}, e:{r:1, c:1}},
            ...vendors.map((_, index) => ({
                s:{r:0, c:2 + index * 4},
                e:{r:0, c:5 + index * 4}
            }))
        ];
        worksheet["!cols"] = [
            {wch:8},
            {wch:28},
            ...vendors.flatMap(() => [{wch:14}, {wch:18}, {wch:14}, {wch:18}])
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Order Tracker");
        XLSX.writeFile(workbook, `admin_order_tracker_${date.replaceAll("/", "_")}.xlsx`);
    };

    return (
        <>
            <PageBodyHeader title="Admin Order Tracker Overview" desc="Overview of store order tracking by vendor">
                <ButtonFrame btn_text="Back" btn_style={{padding:"8px 24px", borderRadius:"8px", border:"1px solid #808280", backgroundColor:"#808280", color:"#FFF", fontSize:"14px", fontWeight:"600"}} onPress={() => navigate(-1)} />
            </PageBodyHeader>

            <div style={{marginTop:"30px", overflowX:"auto", backgroundColor:"#FFF", border:"1px solid #222"}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 18px", borderBottom:"1px solid #222"}}>
                    <strong>{formatMonth(date)}</strong>
                    <div style={{display:"flex", alignItems:"center", gap:"18px"}}>
                        <input value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search stores" style={{padding:"8px 12px", border:"1px solid #999"}} />
                        <ExportData export_type={exportOverview} includes={['excel']} />
                    </div>
                </div>
                <table style={{borderCollapse:"collapse", minWidth:`${220 + vendors.length * 440}px`, width:"100%"}}>
                    <thead>
                        <tr>
                            <th rowSpan={2} style={{border:"1px solid #222", padding:"8px", backgroundColor:"#FFF"}}>NO</th>
                            <th rowSpan={2} style={{border:"1px solid #222", padding:"8px", backgroundColor:"#FFE69A", color:"#C00", minWidth:"220px"}}>{supervisorName.toUpperCase()}</th>
                            {vendors.map((vendor) => <th key={vendor.id} colSpan={4} style={{border:"1px solid #222", padding:"8px", backgroundColor:"#FFC107", fontSize:"18px"}}>{vendor.username}</th>)}
                        </tr>
                        <tr>
                            {vendors.map((vendor) => (
                                ["TARGET", "P.O NUMBER", "VALUE", "ORDER DATE"].map((heading) => <th key={`${vendor.id}-${heading}`} style={{border:"1px solid #222", padding:"8px", backgroundColor:heading === "TARGET" ? "#E2F0D9" : "#FFF2CC", color:heading === "TARGET" ? "#080" : "#111", whiteSpace:"nowrap"}}>{heading}</th>)
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleStores.map((store, index) => (
                            <tr key={store.id || index}>
                                <td style={{border:"1px solid #222", padding:"10px", textAlign:"center", verticalAlign:"top"}}>{index + 1}</td>
                                <td style={{border:"1px solid #222", padding:"10px", fontWeight:"600", verticalAlign:"top"}}>{store.name}</td>
                                {vendors.map((vendor) => {
                                    const storeVendor = getVendorForStore(store, vendor.id);
                                    const tracker = storeVendor?.order_tracker?.[0];
                                    const order = getOrder(storeVendor);
                                    const poFilled = isFilled(order?.po_number);
                                    const valueFilled = isFilled(order?.order_value);
                                    const dateFilled = isFilled(order?.order_date);

                                    return [
                                        <td key={`${vendor.id}-target`} style={{border:"1px solid #222", padding:"10px", backgroundColor:"#E2F0D9", textAlign:"right"}}>{formatNumber(tracker?.target)}</td>,
                                        <td key={`${vendor.id}-po`} style={{border:"1px solid #222", padding:"10px", backgroundColor: poFilled ? "#E2F0D9" : "#FCE4E4"}}>{order?.po_number || "-"}</td>,
                                        <td key={`${vendor.id}-value`} style={{border:"1px solid #222", padding:"10px", textAlign:"right", backgroundColor: valueFilled ? "#E2F0D9" : "#FCE4E4"}}>{formatNumber(order?.order_value)}</td>,
                                        <td key={`${vendor.id}-date`} style={{border:"1px solid #222", padding:"10px", whiteSpace:"nowrap", backgroundColor: dateFilled ? "#E2F0D9" : "#FCE4E4"}}>{order?.order_date || "-"}</td>
                                    ];
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default AdminOrderTrackerOverview;
